import React from "react";
import { motion } from "framer-motion";
import Footer from "./Footer";

const sections = [
  {
    id: "intro",
    title: "Introduction",
    items: [
      "Axtelica Solutions Pvt Ltd is committed to responsible global data management practices",
      "We maintain transparency in how business and user data is handled across products and services",
    ],
  },
  {
    id: "principles",
    title: "Global Data Handling Principles",
    items: [
      "Transparency",
      "Security",
      "Confidentiality",
      "Data minimization",
      "Responsible processing",
      "Compliance with applicable regulations",
    ],
  },
  {
    id: "hosting",
    title: "Data Hosting and Infrastructure",
    items: [
      "Scalability via cloud infrastructure",
      "Reliability through global service providers",
      "Security and high availability systems",
      "Data may be stored or processed in multiple geographic regions",
      "Based on infrastructure availability and operational needs",
    ],
  },
  {
    id: "compliance",
    title: "Compliance Commitment",
    items: [
      "Alignment with internationally recognized privacy standards",
      "Inspired by GDPR (General Data Protection Regulation)",
      "Industry-standard cybersecurity frameworks",
      "Data protection best practices",
      "Compliance varies by jurisdiction and service agreements",
    ],
  },
  {
    id: "ownership",
    title: "Customer Data Ownership",
    items: [
      "Customers retain full ownership of their business data",
      "We do not claim ownership of uploaded content or datasets",
      "Data is used only for delivering requested services",
      "Used for system maintenance and security operations",
    ],
  },
  {
    id: "security",
    title: "Security Measures",
    items: [
      "Encryption of data in transit and at rest",
      "Secure APIs and authentication controls",
      "Access monitoring and threat detection",
      "Infrastructure protection mechanisms",
      "Continuous security improvements",
    ],
  },
  {
    id: "access",
    title: "Data Access Controls",
    items: [
      "Restricted access to authorized personnel only",
      "Approved service providers may access limited data",
      "Technical teams access only when required",
      "Strict confidentiality enforcement",
    ],
  },
  {
    id: "crossborder",
    title: "Cross-Border Transfers",
    items: [
      "Data may be processed across different jurisdictions",
      "Global cloud infrastructure may involve cross-border transfers",
      "Reasonable safeguards are applied during transfers",
    ],
  },
  {
    id: "incident",
    title: "Incident Response",
    items: [
      "Investigation of security incidents",
      "Containment of affected systems",
      "Notification to affected users where appropriate",
      "Continuous improvement of security safeguards",
    ],
  },
  {
    id: "contact",
    title: "Contact Information",
    items: [
      "Axtelica Solutions Pvt Ltd",
      <>
        Email:{" "}
        <a
          href="mailto:info@axtelica.com"
          className="text-blue-600 hover:text-blue-800 underline break-all transition"
        >
          info@axtelica.com
        </a>
      </>,
      <>
        Website:{" "}
        <a
          href="https://axtelica.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline break-all transition"
        >
          Axtelica Official Website
        </a>
      </>,
    ],
  },
];

export default function GlobalDataPolicy() {
  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <div className="w-full overflow-hidden bg-white">
      {/* HERO SECTION */}
      <section
        className="
          relative flex items-center justify-center
          py-14 sm:py-16 lg:py-20
          min-h-[240px] sm:min-h-[320px] lg:min-h-[420px]
          w-full overflow-hidden
          bg-[linear-gradient(-45deg,#101030,#0F1242,#2558BA,#306BCA)]
          bg-[length:400%_400%]
          animate-gradientFlow
        "
      >
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="
            relative z-10
            w-full
            max-w-[1400px]
            px-4 sm:px-6 lg:px-8
            flex justify-center
          "
        >
          <h1
            className="
              text-[30px]
              sm:text-[42px]
              md:text-[52px]
              lg:text-[64px]
              font-semibold
              text-white
              text-center
              leading-tight
              max-w-4xl
            "
          >
            Global Data Policy
          </h1>
        </motion.div>
      </section>

      {/* MAIN CONTENT */}
      <div className="w-full py-8 sm:py-10 lg:py-14 px-3 sm:px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="
            max-w-[1400px]
            mx-auto
            rounded-2xl
            px-6
          "
        >
          {/* SUBTITLE */}
          <p
            className="
              text-center
              text-gray-700
              text-base
              sm:text-lg
              md:text-xl
              lg:text-2xl
              leading-relaxed
              mb-8
              sm:mb-10
              px-2
            "
          >
            Responsible global data management and transparency
            in all operations.
          </p>

          {/* POLICY SECTIONS */}
          <div className="space-y-5 sm:space-y-6">
            {sections.map((sec, index) => (
              <motion.div
                id={sec.id}
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.4,
                  delay: index * 0.05,
                }}
                className="
                  border border-gray-200
                  rounded-2xl
                  p-4
                  sm:p-6
                  lg:p-7
                  bg-white
                  hover:shadow-lg
                  transition-all
                  duration-300
                "
              >
                {/* SECTION TITLE */}
                <h2
                  className="
                    text-xl
                    sm:text-2xl
                    lg:text-2xl
                    
                    text-gray-800
                    mb-4
                    leading-snug
                  "
                >
                  {sec.title}
                </h2>

                {/* LIST ITEMS */}
                <ul
                  className="
                    list-disc
                    pl-5 sm:pl-6
                    space-y-2 sm:space-y-3
                    text-sm
                    sm:text-base
                    lg:text-lg
                    text-gray-600
                    leading-7
                  "
                >
                  {sec.items.map((item, i) => (
                    <li
                      key={i}
                      className="break-words"
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
}